import { useState } from 'react';
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Link,
  Button,
  Tooltip,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Divider,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import HomeIcon from '@mui/icons-material/Home';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import SettingsIcon from '@mui/icons-material/Settings';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import CloseIcon from '@mui/icons-material/Close';

export const Header = ({
  onOpenMobileSidebar,
  company,
  isImpersonating,
  onReturnToSuperAdmin,
  isDarkMode,
  onToggleTheme,
  userRole,
  contentMaxWidth = '56rem', // Default for index/chat page, use '1400px' for admin pages
  contentPadding = '20px', // Default for index/chat page, use '24px' for admin pages
  logoMinWidth = '1500px', // Min viewport width to show logo (1500px for index, 1700px for admin pages)
}) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleMobileMenuToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  // Check if user can access Company Admin (admins and owners only)
  const canAccessCompanyAdmin = userRole === 'super_admin' ||
                                 userRole === 'company_admin' ||
                                 userRole === 'company_owner';

  const navItems = [
    { label: 'Home', href: '/', icon: <HomeIcon /> },
    { label: 'Manage GPTs', href: '/manage-gpts.html', icon: <SmartToyIcon /> },
    ...(canAccessCompanyAdmin ? [{ label: 'Company Admin', href: '/company-admin.html', icon: <SettingsIcon /> }] : []),
  ];

  return (
    <>
      <AppBar
        position="static"
        color="default"
        elevation={0}
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Toolbar
          sx={{
            minHeight: 64,
            px: { xs: 1, sm: 2 },
            position: 'relative',
          }}
        >
          {/* Left side - Logo and mobile menu */}
          <Box sx={{ display: 'flex', alignItems: 'center', zIndex: 1 }}>
            {/* Mobile Menu Button - for chat sidebar */}
            {onOpenMobileSidebar && (
              <IconButton
                onClick={onOpenMobileSidebar}
                sx={{
                  display: { xs: 'flex', md: 'none' },
                  mr: 1,
                  color: 'text.secondary',
                }}
              >
                <MenuIcon />
              </IconButton>
            )}

            {/* Mobile Navigation Menu Button */}
            {!onOpenMobileSidebar && (
              <IconButton
                onClick={handleMobileMenuToggle}
                sx={{
                  display: { xs: 'flex', sm: 'none' },
                  mr: 1,
                  color: 'text.secondary',
                }}
              >
                <MenuIcon />
              </IconButton>
            )}

            {/* Company Logo - always visible on mobile, hidden on tablet/desktop until logoMinWidth */}
            {company?.logo_url && (
              <Link
                href="/"
                sx={{
                  display: 'flex', // Show on mobile
                  alignItems: 'center',
                  minWidth: 0,
                  '@media (min-width: 600px)': {
                    display: 'none', // Hide on tablet/desktop
                  },
                  [`@media (min-width: ${logoMinWidth})`]: {
                    display: 'flex', // Show again on large screens
                  },
                }}
              >
                <Box
                  component="img"
                  src={company.logo_url}
                  alt={`${company.name} logo`}
                  sx={{
                    maxHeight: 48,
                    maxWidth: { xs: 'calc(100vw - 120px)', sm: 300 },
                    objectFit: 'contain',
                  }}
                />
              </Link>
            )}
            {!company?.logo_url && company?.name && (
              <Link
                href="/"
                underline="none"
                sx={{
                  display: 'block', // Show on mobile
                  color: 'text.primary',
                  '&:hover': { color: 'text.secondary' },
                  '@media (min-width: 600px)': {
                    display: 'none', // Hide on tablet/desktop
                  },
                  [`@media (min-width: ${logoMinWidth})`]: {
                    display: 'block', // Show again on large screens
                  },
                }}
              >
                <Typography variant="h6" fontWeight={600}>
                  {company.name}
                </Typography>
              </Link>
            )}
          </Box>

          {/* Center - Menu container aligned with page content */}
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              right: 0,
              top: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'none',
            }}
          >
            <Box
              sx={{
                width: '100%',
                maxWidth: contentMaxWidth,
                display: 'flex',
                alignItems: 'center',
                px: contentPadding,
                pointerEvents: 'auto',
              }}
            >
              <Box sx={{ display: onOpenMobileSidebar ? { xs: 'none', md: 'flex' } : { xs: 'none', sm: 'flex' }, gap: 1 }}>
                {navItems.map((item) => (
                  <Button
                    key={item.href}
                    href={item.href}
                    startIcon={item.icon}
                    sx={{
                      color: 'text.secondary',
                    }}
                  >
                    {item.label}
                  </Button>
                ))}
              </Box>
            </Box>
          </Box>

          {/* Spacer */}
          <Box sx={{ flex: 1 }} />

          {/* Right side - Theme toggle and Return */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, zIndex: 1 }}>
            {/* Theme Toggle Button - desktop only */}
            {onToggleTheme && (
              <Tooltip title={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}>
                <IconButton
                  onClick={onToggleTheme}
                  sx={{
                    color: 'text.secondary',
                    display: { xs: 'none', sm: 'flex' },
                  }}
                >
                  {isDarkMode ? <LightModeIcon /> : <DarkModeIcon />}
                </IconButton>
              </Tooltip>
            )}

            {/* Return to Super Admin Button */}
            {isImpersonating && (
              <Button
                onClick={onReturnToSuperAdmin}
                variant="contained"
                size="small"
                startIcon={<ExitToAppIcon />}
                sx={{
                  bgcolor: '#9333ea',
                  '&:hover': { bgcolor: '#7e22ce' },
                  minWidth: { xs: 'auto', sm: 'auto' },
                  px: { xs: 1, sm: 2 },
                }}
              >
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                  Return
                </Box>
              </Button>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      {/* Mobile Navigation Drawer */}
      <Drawer
        anchor="left"
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        sx={{
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': {
            width: 280,
            bgcolor: 'background.paper',
          },
        }}
      >
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" fontWeight={600}>
            Menu
          </Typography>
          <IconButton onClick={() => setMobileMenuOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider />
        <List>
          {navItems.map((item) => (
            <ListItem key={item.href} disablePadding>
              <ListItemButton
                component="a"
                href={item.href}
                onClick={() => setMobileMenuOpen(false)}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText primary={item.label} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
        <Divider />
        {/* Theme Toggle in Mobile Menu */}
        {onToggleTheme && (
          <List>
            <ListItem disablePadding>
              <ListItemButton
                onClick={() => {
                  onToggleTheme();
                  setMobileMenuOpen(false);
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  {isDarkMode ? <LightModeIcon /> : <DarkModeIcon />}
                </ListItemIcon>
                <ListItemText primary={isDarkMode ? 'Light Mode' : 'Dark Mode'} />
              </ListItemButton>
            </ListItem>
          </List>
        )}
      </Drawer>
    </>
  );
};
